import moment from 'moment'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import accesses from 'modules/core/store/collections/users'
import type Access from 'modules/core/store/models/User'

import type UserForm from 'modules/external_users/lib/forms/User'
import { UpdatePersonalInfoAvatarParams } from 'modules/external_users/store/models/updatePersonalInfoAvatarParams'
import { useUpdatePersonalInfoAvatar } from 'modules/profile/hooks/personal_info/useUpdatePersonalInfoAvatar'

const invite = async (user: Access) => {
  const api = ApiClient()
  await api.post('/invitations', {
    access_id: user.attributes.id,
  })
  user.set({
    invited: true,
  })
}

const getAvatar = (form: UserForm) => {
  if (form.get('avatar').isDirty && form.get('avatar').value) {
    return form.get('avatar').value
  }
  return null
}

const updateAvatar = async (
  user_id: number,
  avatar: string,
  updatePersonalInfoAvatar: (params: UpdatePersonalInfoAvatarParams) => void
) => {
  if (avatar != null) {
    await updatePersonalInfoAvatar({
      accessId: user_id,
      avatar: avatar,
    })
  }
}

interface Props {
  form: UserForm
  user: Access | null | undefined
  refetch: () => Promise<unknown>
}

const useSubmit = ({ form, user, refetch }: Props) => {
  const { updatePersonalInfoAvatar } = useUpdatePersonalInfoAvatar()

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const avatar = getAvatar(form)
      if (user) {
        await form.save(user)
        await updateAvatar(user.id, avatar, updatePersonalInfoAvatar)
      } else {
        form.get('invited_on').set(moment())
        const { id } = await form.create(accesses)

        await updateAvatar(id, avatar, updatePersonalInfoAvatar)
        const foundUser = accesses.get(id)
        if (!foundUser) {
          throw Error(`Error getting users with id = ${id}`)
        }
        invite(foundUser)
      }

      await refetch()
      closePortal()
    }
  )

  return {
    onSubmit,
    submitting,
  }
}

export default useSubmit
